import { Store, createStore, applyMiddleware, Action } from 'redux';
import createSagaMiddleware, { SagaMiddleware } from 'redux-saga';
import { composeWithDevTools } from 'redux-devtools-extension/logOnlyInProduction';
import LogRocket from 'logrocket';
import { ISessionContext } from 'shared-components/lib/authentication';
import { ApplicationState, createRootReducer, rootSaga } from '.';
import errorService from '../services/errorService';
import { fetchErrorsMiddleware } from '../middleware/fetch-error-middleware';
import { createNavigationMiddleware } from '../middleware/navigation-middleware';
import { AuthActionTypes } from './auth/types';
import { history } from '../history';
import * as Sentry from '@sentry/react';

history.listen(() => {
  setTimeout(() => window.scrollTo(0, 0), 0); // timeout added for Chrome issue
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  configureScopeWithState: (scope, state) => {
    let username = '';
    if (state.student?.core?.student_id) {
      username = state.student.core.student_id;
    }
    if (state.student?.offer?.intakeId && state.student?.offer?.offerId) {
      username += `${state.student.offer.intakeId}#${state.student?.offer?.offerId}`;
    }
    scope.setUser({ username });
  },
  stateTransformer: state => {
    // Transform the state to remove sensitive information
    const transformedState = {
      ...state,
      auth: {
        ...state.auth,
        email: null
      },
      config: {
        ...state.config,
        sections: null
      },
      student: {
        ...state.student,
        core: {
          ...state.student.core,
          email: null
        },
        summary: {
          ...state.student.summary,
          email: null
        },
        importData: {
          ...state.student.importData,
          email: null
        }
      },
      ui: {
        ...state.ui,
        sections: null
      }
    };
    return transformedState;
  }
});

let mainTask;
export let sagaMiddleware: SagaMiddleware; // websocket saga injected async after login
export let sagaContext: ISessionContext = {
  getIdToken: () => null,
  onLogout: () => null,
  uni: undefined,
  pathway: undefined,
  isSessionValid: false
};

export function setSagaContext(session: ISessionContext) {
  // https://github.com/redux-saga/redux-saga/issues/2050
  return new Promise((resolve, reject) => {
    try {
      mainTask.setContext({ sagaContext: { ...session } });
      resolve(true);
    } catch (err) {
      reject('Could not load context');
    }
  });
}
export default function configureStore(): Store<ApplicationState> {
  const composeEnhancers = composeWithDevTools({ name: 'CAS Shield: Student' });
  sagaMiddleware = createSagaMiddleware({
    onError: error => {
      errorService.catch(error);
    },
    context: {
      sagaContext: null
    }
  });
  const middlewares = [sagaMiddleware, fetchErrorsMiddleware];

  const store = createStore(
    createRootReducer(),
    composeEnhancers(
      applyMiddleware(
        ...middlewares,
        createNavigationMiddleware(),
        LogRocket.reduxMiddleware({
          stateSanitizer: function (state) {
            return {
              ...state,
              auth: {
                password: undefined
              }
            };
          },
          actionSanitizer: function (action: Action) {
            if (
              action.type === AuthActionTypes.CHANGE_PASSWORD ||
              action.type === AuthActionTypes.CHANGE_PASSWORD_REQUEST ||
              action.type === AuthActionTypes.COGNITO_LOGIN_REQUEST
            ) {
              return null;
            }
            return action;
          }
        })
      ),
      sentryReduxEnhancer
    )
  );

  mainTask = sagaMiddleware.run(rootSaga);
  return store;
}
